import React, { Suspense, useCallback } from 'react';

import { cssMap as unboundCssMap } from '@compiled/react';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import { cssMap, cx } from '@atlaskit/css';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import {
	getSmartLinksAnalyticsContextData,
	onAIAnswerSourceClick,
	SmartLinksAnalyticsContextLocation,
} from '../../../ai-answer-dialog/analytics';
import type { NLPSearchResultSource } from '../../../ai-answer-dialog/types';
import { SainAction, useSAINUsageCriteriaMet } from '../../../common/controller/analytics';
import { SourceCard, SourceFlexibleSmartCard } from '../../../common/ui/ai-sources/source-card';
import { getSAINLayerFrontendMutiArmExperiment } from '../../../common/utils/get-sain-layer-frontend-experiment';

const styles = cssMap({
	sources_container_2_cols: {
		columns: 2,
		columnGap: token('space.200'),
	},
});

const unboundStyles = unboundCssMap({
	sources_container_2_cols: {
		// Adds spacing above all cards except the first card in each column
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'> * + *': {
			marginBlockStart: token('space.200'),
		},
	},
});

export function AITabsSources() {
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const { callbacks, commonAttributes, fireAnalyticsEvent } = useAIAnswerContext();
	const { searchSessionId, sources } = commonAttributes;

	const { cohort: cohortSourcesRedesignExperimentPt2 } = getSAINLayerFrontendMutiArmExperiment({
		abTestId: 'kd_sain_sources_redesign_pt_2',
	});

	const sourceClickCallback = useCallback(
		(index: number, source: NLPSearchResultSource, hostname?: string) => {
			fireAnalyticsEvent(
				onAIAnswerSourceClick({
					sourceIndex: index,
					hostname,
					sourceProduct: source.product || '',
					...commonAttributes,
				}),
			);
			fireSAINUsageEvent({
				metCriteria: SainAction.SOURCE_CLICKED,
				commonAttributes,
				sourceProduct: source.product || '',
			});
			callbacks?.onAIAnswerSourceClick?.();
		},
		[fireAnalyticsEvent, commonAttributes, callbacks, fireSAINUsageEvent],
	);

	return (
		<Suspense fallback={<></>}>
			<AnalyticsContext
				data={getSmartLinksAnalyticsContextData({
					location: SmartLinksAnalyticsContextLocation.SAIN_Q_AND_A_CARD,
					commonAttributes,
				})}
			>
				<SmartCardProvider>
					{cohortSourcesRedesignExperimentPt2 === 'sources_tab_redesign' ? (
						<Box
							xcss={cx(
								sources.length > 1 && styles.sources_container_2_cols,
								sources.length > 1 && unboundStyles.sources_container_2_cols,
							)}
						>
							{sources.map((source, index) => (
								<SourceFlexibleSmartCard
									index={index}
									key={`search-ai-answer-source-${index}`}
									source={source}
									searchSessionId={searchSessionId}
									onClickHandler={sourceClickCallback}
								/>
							))}
						</Box>
					) : (
						<Inline alignBlock="center" rowSpace="space.100" shouldWrap={true} space="space.050">
							{sources.map((source, index) => (
								<SourceCard
									index={index}
									key={`search-ai-answer-source-${index}`}
									source={source}
									searchSessionId={searchSessionId}
									onClickHandler={sourceClickCallback}
									enableSmartLinkSources={true}
								/>
							))}
						</Inline>
					)}
				</SmartCardProvider>
			</AnalyticsContext>
		</Suspense>
	);
}
